import './NewWorkTickets.scss';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import DataTable from '../component/UI/DataTable';

import { getNewWorkTickets, getNumNewWorkTicketPages, getLoadingNewWorkTickets } from 'src/module/workTicket/selector';
import { fetchNewWorkTickets } from 'src/module/workTicket/action';
import { getClientId, getSingularWorkTicketLabel } from 'src/module/authentication/selector';
import { getDevicesSelectedFiltersObj } from 'src/module/filter/selector';
import DeviceLink from './UI/DeviceLink';
import Tooltip from '@mui/material/Tooltip';

import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { formatAlarmLabel } from 'src/component/AlarmsList';
import { Badge } from '@mui/material';
import { List } from 'immutable';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceRotate from 'src/component/UI/DeviceRotate';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

export default function NewWorkTickets () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const clientId = useSelector(getClientId);
  const selectedFilters = useSelector(getDevicesSelectedFiltersObj, isEqual);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sort, setSort] = useState<string | null>(null);

  useEffect(() => {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    dispatch(fetchNewWorkTickets(sevenDaysAgo, Map(selectedFilters), true, page, pageSize, null, sort));
  }, [clientId, selectedFilters, page, pageSize, sort]);

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setSort(_sort);
  };

  const newWorkTickets = useSelector(getNewWorkTickets);
  const numPages = useSelector(getNumNewWorkTicketPages);
  const loading = useSelector(getLoadingNewWorkTickets);
  const singularWorkTicketLabel = useSelector(getSingularWorkTicketLabel);
  const newSapNotificationData: any = [];
  newWorkTickets.forEach((alarm:any ) => {
    const devicesRaw = alarm.get('devices');
    const devices = devicesRaw?.toJS ? devicesRaw.toJS() : (Array.isArray(devicesRaw) ? devicesRaw : []);
    const deviceRows = Array.from(
      new Map(
        devices
          .filter((d: any) => d?.id && d?.tag)
          .map((d: any) => [d.id, d])
      ).values()
    ).map((d: any) => ({
      circuitTag: d.type === 'circuit' ? d.tag : null,
      circuitParentTag: d.type === 'circuit' ? d.parent_tag : null,
      controllerTag: d.type === 'controller' ? d.tag : (d.parent_type === 'controller' ? d.parent_tag : null),
    }));

    newSapNotificationData.push({
      sapNotification: alarm.get('sap_notification_id'),
      alarms: alarm.get('alarm'),
      deviceRows,
    });
  });

  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [showRotateMessage, setShowRotateMessage] = useState(isMobile && isPortrait);

  useEffect(() => {
    setShowRotateMessage(isMobile && isPortrait);
  }, [isMobile, isPortrait]);

  const alarm = (alarms) => {
    if ( !alarms || alarms==="") {
      return "";
    }

    const numAlarms = (alarms.match(/,/g) || []).length + 1;
    if (numAlarms < 3) {
      return formatAlarmLabel(alarms, t);
    }

    return (
      <Tooltip title={`${formatAlarmLabel(alarms, t)}`} className='alarm' data-testid='device-link'>
        <div className='alarm-badge'>
          <Badge badgeContent={numAlarms} color="primary" className='alarmBadge'>
            <NotificationsActiveIcon fontSize='large' className='bell' />
          </Badge>
        </div>
      </Tooltip>
    );
  };

  return (
    showRotateMessage ? <DeviceRotate /> :
      <DataTable
        pagination
        paginationPerPage={5}
        paginationRowsPerPageOptions={[5, 20, 50]}
        fetchPage={fetchPage}
        numPages={numPages}
        loading={loading}
        columns={[
          {
            id: 'sap_notifications.work_ticket_number',
            Header: t('workTickets.ticketNo', { label: singularWorkTicketLabel || t('workTickets.singular') }),
            disableSortBy: true,
            accessor: (row: any) => row.sapNotification,
          },
          {
            id: 'sap_notifications.circuit',
            Header: t('workTickets.circuit'),
            width: '80px',
            accessor: (row: any) => {
              if (!row.deviceRows.length) return 'N/A';
              return (
                <div className='tag-list'>
                  {row.deviceRows.map((d: any, i: number) => (
                    d.circuitTag
                      ? <DeviceLink key={i} label={d.circuitTag} circuitTag={d.circuitTag} controllerTag={d.circuitParentTag} />
                      : <span key={i} className='tag-na'>N/A</span>
                  ))}
                </div>
              );
            }
          },
          {
            id: 'controller',
            Header: t('workTickets.controller'),
            width: '80px',
            accessor: (row: any) => {
              if (!row.deviceRows.length) return 'N/A';
              return (
                <div className='tag-list'>
                  {row.deviceRows.map((d: any, i: number) => (
                    d.controllerTag
                      ? <DeviceLink key={i} label={d.controllerTag} controllerTag={d.controllerTag} />
                      : <span key={i} className='tag-na'>N/A</span>
                  ))}
                </div>
              );
            }
          },
          {
            id: 'active_alarms',
            Header: t('workTickets.activeAlarms'),
            accessor: (row: any) => row.alarms,
            Cell: (row: any) => alarm(row.cell.value) || '',

          },
        ]}
        data={List(newSapNotificationData)}
        tableId='newWorkTicketsTable'
      />
  );
}

NewWorkTickets.propTypes = {
};

NewWorkTickets.defaultProps = {
};
